import React, { useRef } from "react";
import emailjs from "@emailjs/browser";
import { Cpu, ShieldCheck, Zap } from "lucide-react";
import "./INquiryForm.css";

const InquiryForm = () => {
  const form = useRef();
  const service_id = "service_odgpbua";
  const template_id = "template_py8ya35";
  const public_key = "QctNiP4iDIGr8FHyo";

  const sendEmail = (e) => {
    e.preventDefault();

    emailjs
      .sendForm(service_id, template_id, form.current, public_key)
      .then(
        (result) => {
          console.log("SUCCESS!", result.text);
          alert("Your inquiry has been sent successfully!");
          form.current.reset();
        },
        (error) => {
          console.log("FAILED...", error.text);
          alert("Failed to send inquiry. Please try again.");
        }
      );
  };

  return (
    <section className="inquiry-section-light" id="contact">
      <div className="container reveal" style={{ position: 'relative', zIndex: 1 }}>
        <div className="mission-contact-grid">
          
          {/* Left Column: The Mission (Light Theme) */}
          <div className="reveal-left mission-content">
            <span style={{
              display: 'inline-block',
              padding: '0.5rem 1rem',
              backgroundColor: 'var(--accent-slate)',
              borderRadius: '4px',
              fontSize: '0.75rem',
              fontWeight: 700,
              color: 'var(--white)',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
              marginBottom: '1.5rem'
            }}>
              The Mission
            </span>

            {/* Decorative editorial line */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              margin: '1.25rem 0 1.5rem'
            }}>
              <div style={{
                width: '40px',
                height: '2px',
                background: 'linear-gradient(90deg, var(--accent-slate), transparent)',
                borderRadius: '2px'
              }} />
              <div style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: '#3B82F6',
                opacity: 0.6
              }} />
            </div>
            
            <h2 style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.5rem)',
              fontWeight: 800,
              marginBottom: '1.5rem',
              lineHeight: 1.1,
              letterSpacing: '-0.04em',
              color: 'var(--accent-slate)'
            }}>
              Engineering <br />
              <span style={{ color: 'var(--text-muted)' }}>Digital Gravity.</span>
            </h2>

            <div style={{
              fontSize: '1.15rem',
              lineHeight: 1.8,
              color: 'var(--text-muted)',
              fontWeight: 400
            }}>
              <p style={{ marginBottom: '1.25rem' }}>
                AutoMachina was born at the intersection of brute-force engineering and artistic digital craftsmanship. We don't just build software; we build systems that endure.
              </p>
              <p>
                Based in the heart of innovation, we serve as the technical engine for companies looking to move faster and stay secure in an AI-dominated landscape.
              </p>
            </div>

            {/* Feature Highlights */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem', marginTop: '2.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <div style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '12px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#3B82F6',
                  flexShrink: 0
                }}>
                  <Zap size={20} />
                </div>
                <div>
                  <h4 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--accent-slate)', marginBottom: '0.2rem' }}>AI-First Engineering</h4>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>Custom models & autonomous workflows designed for enterprise scale.</p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <div style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '12px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#3B82F6',
                  flexShrink: 0
                }}>
                  <ShieldCheck size={20} />
                </div>
                <div>
                  <h4 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--accent-slate)', marginBottom: '0.2rem' }}>Enterprise Security</h4>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>Privacy-first architectures ensuring complete data governance.</p>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                <div style={{
                  width: '42px',
                  height: '42px',
                  borderRadius: '12px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  border: '1px solid rgba(59, 130, 246, 0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#3B82F6',
                  flexShrink: 0
                }}>
                  <Cpu size={20} />
                </div>
                <div>
                  <h4 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--accent-slate)', marginBottom: '0.2rem' }}>Digital Craftsmanship</h4>
                  <p style={{ fontSize: '0.9rem', color: 'var(--text-muted)', lineHeight: 1.5 }}>Artisanal code precision meeting high-frequency performance.</p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Contact Form (Executive Dark Card Widget) */}
          <div className="reveal-right">
            <div className="inquiry-container-dark">
              <div className="form-header">
                <span className="form-badge-dark">Get In Touch</span>
                <h3 className="form-title-dark">Let's Build the Future.</h3>
                <p className="form-subtitle-dark">
                  Ready to engineer your digital gravity? Fill out the form below.
                </p>
              </div>

              <form className="inquiry-form" ref={form} onSubmit={sendEmail}>
                <div className="inquiry-group">
                  <label htmlFor="number">Communication Number</label>
                  <input type="tel" id="number" name="number" placeholder="+1 (555) 000-0000" required />
                </div>

                <div className="inquiry-group">
                  <label htmlFor="email">Email Address</label>
                  <input type="email" id="email" name="email" placeholder="name@company.com" required />
                </div>

                <div className="inquiry-group full-width">
                  <label htmlFor="message">Your Vision</label>
                  <textarea id="message" name="message" rows="4" placeholder="Tell us about your project or challenges..." required></textarea>
                </div>

                <button type="submit" className="submit-btn-dark">
                  Launch Inquiry
                </button>
              </form>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default InquiryForm;
